import dayjs from 'dayjs';
import type {
  AssistantChartResponse,
  AssistantTableResponse,
  MessageResponse,
} from 'src/api/ProactApiClient';
import {
  assistantToolLabel,
  showsToolRow,
} from 'src/app/assistant/assistantToolLabels';
import {
  CHART_UNAVAILABLE_TEXT,
  chartDataTable,
} from 'src/app/assistant/Chart/chatChartFormat';
import {
  TABLE_EMPTY_TEXT,
  tableDataGrid,
  tableTruncationText,
} from 'src/app/assistant/Table/chatTableFormat';
import {stripMemoryMarkers} from 'src/shared/assistant/memoryMarkers';
import {formatUtcDateTime} from 'src/shared/utils/formatters';

export const UNTITLED_TRANSCRIPT_TITLE = 'Scout conversation';
export const TRUNCATED_REPLY_TEXT = 'This reply was cut short.';

export type TranscriptSegment =
  | {kind: 'text'; text: string}
  | {kind: 'tools'; labels: string[]}
  | {kind: 'chart'; chart: AssistantChartResponse}
  | {kind: 'table'; table: AssistantTableResponse};

export interface TranscriptEntry {
  key: number;
  role: 'user' | 'assistant';
  createdAt: Date | string;
  segments: TranscriptSegment[];
  truncated: boolean;
}

export interface ChatTranscript {
  title: string;
  exportedAt: Date;
  entries: TranscriptEntry[];
}

export const transcriptRoleLabel = (role: TranscriptEntry['role']): string =>
  role === 'user' ? 'You' : 'Scout';

const rowSegments = (message: MessageResponse): TranscriptSegment[] => {
  const segments: TranscriptSegment[] = [];
  const content = message.content ?? '';
  const text =
    message.role.toLowerCase() === 'user' ? content : stripMemoryMarkers(content);
  if (text.trim().length > 0) segments.push({kind: 'text', text});
  const labels = (message.toolCalls ?? [])
    .filter(call => showsToolRow(call.name))
    .map(call => assistantToolLabel(call.name));
  if (labels.length > 0) segments.push({kind: 'tools', labels});
  for (const chart of message.charts ?? []) {
    segments.push({kind: 'chart', chart});
  }
  for (const table of message.tables ?? []) {
    segments.push({kind: 'table', table});
  }
  return segments;
};

/**
 * Folds a conversation's message rows into one entry per turn: consecutive assistant rows
 * (tool-loop iterations plus the final reply) become a single Scout entry. Raw tool input never appears.
 */
export const buildTranscriptEntries = (
  messages: MessageResponse[]
): TranscriptEntry[] => {
  const entries: TranscriptEntry[] = [];
  for (const message of messages) {
    const role = message.role.toLowerCase() === 'user' ? 'user' : 'assistant';
    const previous = entries[entries.length - 1];
    if (role === 'assistant' && previous?.role === 'assistant') {
      previous.segments.push(...rowSegments(message));
      previous.truncated = previous.truncated || message.truncated;
      continue;
    }
    entries.push({
      key: message.id,
      role,
      createdAt: message.createdAt,
      segments: rowSegments(message),
      truncated: message.truncated,
    });
  }
  return entries;
};

const markdownCell = (value: string): string =>
  value.replace(/\r?\n/g, ' ').replace(/\|/g, '\\|');

const markdownRow = (cells: string[]): string =>
  `| ${cells.map(markdownCell).join(' | ')} |`;

const chartToMarkdown = (chart: AssistantChartResponse): string => {
  const title = `**${chart.title}**`;
  const table = chartDataTable(chart);
  if (!table) return `${title}\n\n_${CHART_UNAVAILABLE_TEXT}_`;
  return [
    title,
    '',
    markdownRow(table.header),
    `| --- |${' ---: |'.repeat(table.header.length - 1)}`,
    ...table.rows.map(markdownRow),
  ].join('\n');
};

const tableToMarkdown = (table: AssistantTableResponse): string => {
  const grid = tableDataGrid(table);
  const parts = table.title ? [`**${table.title}**`] : [];
  if (grid.rows.length === 0) {
    if (!table.truncated) parts.push(`_${TABLE_EMPTY_TEXT}_`);
  } else {
    parts.push(
      [
        markdownRow(grid.header),
        `|${grid.numeric.map(numeric => (numeric ? ' ---: |' : ' --- |')).join('')}`,
        ...grid.rows.map(markdownRow),
      ].join('\n')
    );
  }
  if (table.truncated) parts.push(`_${tableTruncationText(grid.rows.length)}_`);
  return parts.join('\n\n');
};

const entryToMarkdown = (entry: TranscriptEntry): string => {
  const parts = [
    `### ${transcriptRoleLabel(entry.role)} · ${formatUtcDateTime(entry.createdAt)}`,
  ];
  for (const segment of entry.segments) {
    if (segment.kind === 'text') parts.push(segment.text);
    else if (segment.kind === 'tools')
      parts.push(segment.labels.map(label => `- ${label}`).join('\n'));
    else if (segment.kind === 'chart')
      parts.push(chartToMarkdown(segment.chart));
    else parts.push(tableToMarkdown(segment.table));
  }
  if (entry.truncated) parts.push(`> _${TRUNCATED_REPLY_TEXT}_`);
  return parts.join('\n\n');
};

/**
 * Markdown document for a Scout conversation: title, export date, then each turn with its
 * role and time, reply text, tool activity lines, charts as data tables, and Scout tables.
 * @registry Pure Scout transcript → Markdown formatter used by the chat export menu.
 */
export const buildTranscriptMarkdown = (transcript: ChatTranscript): string => {
  const header = `# ${transcript.title}\n\n- **Exported:** ${formatUtcDateTime(
    transcript.exportedAt
  )}`;
  const body = transcript.entries.map(entryToMarkdown);
  return `${[header, ...body].join('\n\n---\n\n')}\n`;
};

const slugify = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');

/** File name stem shared by the Markdown download and the print document title. */
export const transcriptFileStem = (title: string, exportedAt: Date): string =>
  `${slugify(title) || 'scout-conversation'}-${dayjs(exportedAt).format('YYYY-MM-DD')}`;
